import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import LegalFooter from '../../components/shared/LegalFooter';
import DashboardSearch from '../../components/shared/DashboardSearch';
import '../../styles/global.css';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const PIE_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];

const CHART_VIEWS = ['signups', 'revenue'];

export default function EmployeeDashboard() {
  const [data, setData] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeChart, setActiveChart] = useState('signups');
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/employee/dashboard'),
      api.get('/employee/dashboard/charts'),
    ])
      .then(([dashRes, chartRes]) => {
        setData(dashRes.data);
        setCharts(chartRes.data);
      })
      .catch(() => toast.error('Could not load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const { stats, recent_signups, recent_tickets, upcoming_vacancies } = data || {};

  return (
    <div className="app-layout">
      <Topbar role="employee" />
      <div className="dashboard-layout">
        <EmployeeSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Operations Dashboard</div>
          <div className="page-sub">Platform-wide overview · {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>

          <DashboardSearch endpoint="/employee/search" />

          {/* Alert banners */}
          {stats?.tenants_no_payment > 0 && (
            <div style={{ background: '#fff5e0', border: '1px solid #f5c060', borderRadius: 12, padding: '12px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <strong style={{ color: '#b85c00' }}>⚠️ {stats.tenants_no_payment} tenant{stats.tenants_no_payment !== 1 ? 's' : ''} with no payment method</strong>
                <p style={{ fontSize: 12, color: 'var(--text-sub)', marginTop: 2 }}>Self-signup tenants who have not added a card or bank account (bulk imported tenants excluded).</p>
              </div>
              <button className="btn btn-sm btn-outline" onClick={() => navigate('/employee/payment-alerts')}>View &amp; Send Reminders</button>
            </div>
          )}

          {stats?.open_tickets > 0 && (
            <div style={{ background: '#ffeaea', border: '1px solid #f5a0a0', borderRadius: 12, padding: '12px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <strong style={{ color: 'var(--red)' }}>🎫 {stats.open_tickets} open support ticket{stats.open_tickets !== 1 ? 's' : ''}</strong>
                <p style={{ fontSize: 12, color: 'var(--text-sub)', marginTop: 2 }}>Tenants are waiting for a response.</p>
              </div>
              <button className="btn btn-sm btn-outline" onClick={() => navigate('/employee/tickets')}>View Tickets</button>
            </div>
          )}

          {/* Stat cards */}
          <div className="metric-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
            <div className="metric-card"><div className="metric-label">Total landlords</div><div className="metric-value">{stats?.total_landlords || 0}</div></div>
            <div className="metric-card"><div className="metric-label">Active properties</div><div className="metric-value accent">{stats?.total_properties || 0}</div></div>
            <div className="metric-card"><div className="metric-label">Active tenants</div><div className="metric-value green">{stats?.total_active_tenants || 0}</div></div>
            <div className="metric-card"><div className="metric-label">Revenue MTD</div><div className="metric-value sm">${fmt(stats?.revenue_mtd)}</div></div>
            <div className="metric-card"><div className="metric-label">Open tickets</div><div className="metric-value" style={{ color: stats?.open_tickets > 0 ? 'var(--red)' : 'var(--green)' }}>{stats?.open_tickets || 0}</div></div>
            <div className="metric-card"><div className="metric-label">No payment method</div><div className="metric-value" style={{ color: stats?.tenants_no_payment > 0 ? 'var(--orange)' : 'var(--green)' }}>{stats?.tenants_no_payment || 0}</div></div>
          </div>

          {/* ── Charts row ── */}
          {charts && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>

              {/* Left: toggleable bar/line chart */}
              <div className="card" style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div className="card-title" style={{ margin: 0 }}>
                    {activeChart === 'signups' ? 'Tenant Signups' : 'Monthly Revenue'} — Last 6 Months
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {CHART_VIEWS.map(v => (
                      <button key={v} onClick={() => setActiveChart(v)} style={{
                        padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer', border: 'none',
                        background: activeChart === v ? '#2563eb' : '#f1f5f9',
                        color: activeChart === v ? '#fff' : '#64748b',
                      }}>
                        {v === 'signups' ? 'Signups' : 'Revenue'}
                      </button>
                    ))}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  {activeChart === 'signups' ? (
                    <BarChart data={charts.signups_by_month} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" name="Signups" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  ) : (
                    <LineChart data={charts.revenue_by_month} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => '$' + v.toLocaleString()} />
                      <Tooltip formatter={v => '$' + parseFloat(v).toFixed(2)} />
                      <Line type="monotone" dataKey="total" name="Revenue" stroke="#16a34a" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>

              {/* Right: two pie charts stacked */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Tenant status pie */}
                <div className="card" style={{ padding: '20px 24px', flex: 1 }}>
                  <div className="card-title" style={{ marginBottom: 12 }}>Tenant Status Breakdown</div>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={charts.tenant_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={({ name, percent }) => name + ' ' + (percent * 100).toFixed(0) + '%'} labelLine={false} fontSize={11}>
                        {charts.tenant_status.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* ISP breakdown pie */}
                <div className="card" style={{ padding: '20px 24px', flex: 1 }}>
                  <div className="card-title" style={{ marginBottom: 12 }}>Properties by ISP</div>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={charts.isp_breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={({ name, percent }) => name + ' ' + (percent * 100).toFixed(0) + '%'} labelLine={false} fontSize={11}>
                        {charts.isp_breakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

              </div>
            </div>
          )}

          {/* Recent tables */}
          <div className="two-col">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Recent tenant signups</div>
                <button className="btn btn-outline btn-sm" onClick={() => navigate('/employee/tenants')}>View all</button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Tenant</th><th>Property</th><th>Unit</th><th>Date</th></tr></thead>
                  <tbody>
                    {(!recent_signups || recent_signups.length === 0) && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No signups yet</td></tr>
                    )}
                    {recent_signups?.map((s, i) => (
                      <tr key={i}>
                        <td><div style={{ fontWeight: 600 }}>{s.first_name} {s.last_name}</div><div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{s.email}</div></td>
                        <td style={{ fontSize: 12 }}>{s.property_name}</td>
                        <td style={{ fontWeight: 700 }}>{s.unit_number || '—'}</td>
                        <td style={{ fontSize: 12 }}>{fmtDate(s.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">Recent support tickets</div>
                <button className="btn btn-outline btn-sm" onClick={() => navigate('/employee/tickets')}>View all</button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Tenant</th><th>Subject</th><th>Status</th></tr></thead>
                  <tbody>
                    {(!recent_tickets || recent_tickets.length === 0) && (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No tickets</td></tr>
                    )}
                    {recent_tickets?.map((t, i) => (
                      <tr key={i}>
                        <td><div style={{ fontWeight: 600, fontSize: 13 }}>{t.tenant_name}</div><div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{t.property_name}</div></td>
                        <td style={{ fontSize: 12 }}>{t.subject}</td>
                        <td><span className={'badge badge-' + (t.status === 'resolved' ? 'green' : t.status === 'open' ? 'blue' : 'orange')}>{t.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Upcoming Vacancies */}
          {upcoming_vacancies && upcoming_vacancies.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Upcoming Vacancies (next 90 days)</div>
                <button className="btn btn-outline btn-sm" onClick={() => navigate('/employee/tenants')}>View all tenants</button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Property</th>
                      <th>Unit</th>
                      <th>Lease End</th>
                      <th>Days Left</th>
                      <th>Lease Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcoming_vacancies.map((v, i) => {
                      const days = parseInt(v.days_remaining);
                      const isExpired = days < 0;
                      return (
                        <tr key={i}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{v.first_name} {v.last_name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{v.email}</div>
                          </td>
                          <td style={{ fontSize: 12 }}>{v.property_name}</td>
                          <td style={{ fontWeight: 700 }}>{v.unit_number || '—'}</td>
                          <td style={{ fontSize: 12 }}>{fmtDate(v.lease_end)}</td>
                          <td>
                            <span className={`badge badge-${isExpired ? 'red' : days <= 30 ? 'orange' : 'blue'}`}>
                              {isExpired ? `${Math.abs(days)}d overdue` : `${days}d`}
                            </span>
                          </td>
                          <td style={{ fontSize: 12 }}>{v.lease_category || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Quick actions */}
          <div className="card">
            <div className="card-header"><div className="card-title">Quick actions</div></div>
            <div className="card-body" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => navigate('/employee/campaigns')}>📧 New email campaign</button>
              <button className="btn btn-outline" onClick={() => navigate('/employee/payment-alerts')}>⚠️ Send payment reminders</button>
              <button className="btn btn-outline" onClick={() => navigate('/employee/documents')}>📄 Upload document</button>
              <button className="btn btn-outline" onClick={() => navigate('/employee/service')}>📡 Manage service</button>
              <button className="btn btn-outline" onClick={() => navigate('/employee/landlords')}>🏢 Assign account managers</button>
            </div>
          </div>

          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
